import { Form, Input, InputNumber, Checkbox, Button, Space, message, Select } from "antd";
import { useState, useEffect } from "react";
import { adjacentCropService } from "../../../services/api";
import type { CropSpecies, CropVariety } from "../../../types/adjacent-crop";

const { TextArea } = Input;

interface AdjacentCropFormProps {
  auditId: number;
  farmId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * AdjacentCropForm Component
 * Form for creating a new adjacent crop from an audit
 */
export const AdjacentCropForm = ({ auditId, onSuccess, onCancel }: AdjacentCropFormProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [species, setSpecies] = useState<CropSpecies[]>([]);
  const [varieties, setVarieties] = useState<CropVariety[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);

  useEffect(() => {
    loadSpecies();
  }, []);

  useEffect(() => {
    if (selectedSpecies) {
      loadVarieties(selectedSpecies);
    } else {
      setVarieties([]);
      form.setFieldValue('variety', undefined);
    }
  }, [selectedSpecies]);

  const loadSpecies = async () => {
    setLoadingSpecies(true);
    try {
      const data = await adjacentCropService.getCropSpecies();
      setSpecies(data);
    } catch (error: any) {
      message.error("Error al cargar especies");
    } finally {
      setLoadingSpecies(false);
    }
  };

  const loadVarieties = async (speciesId: string) => {
    setLoadingVarieties(true);
    try {
      const response = await adjacentCropService.getVarietiesBySpecies(speciesId);
      setVarieties(response.varieties || []);
    } catch (error: any) {
      message.error("Error al cargar variedades");
      setVarieties([]);
    } finally {
      setLoadingVarieties(false);
    }
  };

  const handleSpeciesChange = (value: string) => {
    setSelectedSpecies(value);
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData = {
        audit_id: auditId,
        species: values.species,
        variety: values.variety || undefined,
        is_producer_owner: values.is_producer_owner || false,
        comment: values.comment,
        estimated_surface: values.estimated_surface || 0,
      };

      await adjacentCropService.createFromAudit(formData);
      onSuccess();
    } catch (error: any) {
      message.error(error.message || "Error al crear cultivo adyacente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        is_producer_owner: false,
        estimated_surface: 0,
      }}
    >
      <Form.Item
        label="Especie"
        name="species"
        rules={[{ required: true, message: "Por favor seleccione una especie" }]}
      >
        <Select
          showSearch
          placeholder="Seleccione una especie"
          loading={loadingSpecies}
          onChange={handleSpeciesChange}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={species.map(s => ({
            value: s.id,
            label: s.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        label="Variedad (opcional)"
        name="variety"
      >
        <Select
          showSearch
          placeholder="Seleccione una variedad o deje en blanco si es desconocida"
          loading={loadingVarieties}
          disabled={!selectedSpecies}
          allowClear
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={varieties.map(v => ({
            value: v.id,
            label: v.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        label="Superficie Estimada (ha)"
        name="estimated_surface"
        rules={[
          { required: true, message: "Por favor ingrese la superficie estimada" },
          { type: 'number', min: 0, message: "La superficie debe ser mayor o igual a 0" }
        ]}
      >
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          step={0.01}
          precision={2}
          placeholder="0.00"
        />
      </Form.Item>

      <Form.Item
        name="is_producer_owner"
        valuePropName="checked"
      >
        <Checkbox>¿Es propiedad del productor?</Checkbox>
      </Form.Item>

      <Form.Item
        label="Comentario"
        name="comment"
        // Optional — saves with an empty comment when the polygon
        // and species/variety are self-explanatory.
      >
        <TextArea
          rows={4}
          placeholder="Describa el cultivo adyacente detectado (opcional)..."
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Crear
          </Button>
          <Button onClick={onCancel}>
            Cancelar
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
